import React, { useEffect, useState, useContext } from 'react';
import {
  Typography,
  Link,
  IconButton,
  Dialog,
  Tab,
  Tabs,
  Card,
  CardContent,
  CardActions,
  Box,
  useMediaQuery,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import { Edit, Delete, CalendarToday, Person } from '@mui/icons-material';
import ExpenseForm from './ExpenseForm';
import UserDetailsContext from '../Dashboard/UserDetailsContext';
import { useLocation } from 'react-router-dom';
import Loading from '../Alert/Loading';// ✅ Import your Loading component
import UseFeatureFlags from '../components/UseFeatureFlags';
import { useExpenseStore } from '../store/useExpenseStore';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import Fuse from 'fuse.js';
import TextField from '@mui/material/TextField';

export default function ExpenseList({ onEdit, onApprove, onReject, isAdmin }) {
  const location = useLocation();
  const [expenses, setExpenses] = useState([]);
  const [editExpense, setEditExpense] = useState(null);
  const [viewingReceiptURL, setViewingReceiptURL] = useState(null);
  const [filterStatus, setFilterStatus] = useState('PENDING');
  const [loading, setLoading] = useState(true); // ✅ Loading state
  const [showFilterTabs, setShowFilterTabs] = useState(true); // Track filter tabs visibility
  const [search, setSearch] = useState('');
  const { userDetails } = useContext(UserDetailsContext);
  const isMobile = useMediaQuery('(max-width:600px)');
   const [businessInfo, setBusinessInfo] = useState(null);
   const [businessCurrency, setBusinessCurrency] = useState(null);
   const { hasFeature } = UseFeatureFlags();
   console.log('User D:', userDetails);
   const StyledTab = styled(Tab)({
  textTransform: 'none',
});
   
   // Debug feature flags
   console.log('Feature flags in ExpenseList:', {
     edit_expense: hasFeature('edit_expense'),
     delete_expense: hasFeature('delete_expense'),
     resubmit_expense: hasFeature('resubmit_expense')
   });
   console.log('User Details krishna:', userDetails);

  const { loadExpensesByTeamContact, loadExpensesByBusiness, getBusinessExpenses, getTeamExpenses, deleteExpense, loadDashboardSummaries, getBusinessCurrency, loadBusinessCurrency } = useExpenseStore();

useEffect(() => {
  const isOwner = userDetails?.UserRole.toLowerCase() === 'manager'|| userDetails?.UserRole.toLowerCase() === 'owner';
  let mounted = true;

  (async () => {
    if (isOwner) {
      const businessId = userDetails?.BusinessID;
      if (!businessId) return;
      await useExpenseStore.getState().loadExpensesByBusiness(businessId);
      if (!mounted) return;
      const store = useExpenseStore.getState();
      setExpenses(store.getBusinessExpenses(businessId));
    } else {
      const teamId = userDetails?.TeamContactID;
      if (!teamId) return;
      await useExpenseStore.getState().loadExpensesByTeamContact(teamId);
      if (!mounted) return;
      const store = useExpenseStore.getState();
      setExpenses(store.getTeamExpenses(teamId));
    }
  })();

  return () => { mounted = false; };
}, [userDetails?.UserRole, userDetails?.BusinessID, userDetails?.TeamContactID]);

  // Handle scroll events for filter tabs visibility
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateFilterTabsVisibility = () => {
      const currentScrollY = window.scrollY;
      
      // Show tabs when at top, hide when scrolling down
      if (currentScrollY <= 10) {
        setShowFilterTabs(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 150) {
        setShowFilterTabs(false);
      } else if (currentScrollY < lastScrollY && currentScrollY < 100) {
        setShowFilterTabs(true);
      }
      
      lastScrollY = currentScrollY;
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateFilterTabsVisibility);
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchExpenses = async () => {
    const { TeamContactID } = userDetails || {};
    if (!TeamContactID) return;
    setLoading(true);
     await loadExpensesByTeamContact(TeamContactID, { force: true });
     await loadBusinessCurrency(userDetails?.BusinessID, { force: true });
    const store = useExpenseStore.getState();
    setExpenses(store.getTeamExpenses(TeamContactID));
    setLoading(false);
  };

 const deleteExpenseLocal = async (expenseID, expenseName) => {
  toast(
    ({ closeToast }) => (
      <div>
        <div style={{ marginBottom: 8 }}>
          <strong>Delete Expense?</strong>
          <div>Are you sure you want to delete this expense?</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={async () => {
              toast.dismiss('delete-expense-confirm');
              try {
                await deleteExpense({ ExpenseID: expenseID, TeamContactID: userDetails?.TeamContactID });

                // Fetch team expenses
                const store = useExpenseStore.getState();
                setExpenses(store.getTeamExpenses(userDetails?.TeamContactID));

                // Fetch dashboard summary based on role
                const userRole = userDetails?.UserRole;
                const businessId = userDetails?.BusinessID;
                const teamContactId = userDetails?.TeamContactID;

                await loadDashboardSummaries({
                  BusinessID: businessId,
                  TeamContactID: teamContactId,
                  UserRole: userRole,
                }, { force: true });

                toast.success("Expense has been successfully deleted.");
              } catch (error) {
                toast.error("Failed to delete expense due to a server error.");
              }
            }}
          >
            Delete
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={closeToast}
          >
            Cancel
          </Button>
        </div>
      </div>
    ),
    { autoClose: false, toastId: 'delete-expense-confirm' }
  );
};
  

  const handleEdit = (expense) => setEditExpense(expense);
  useEffect(() => {
    const businessId = userDetails?.BusinessID;
    if (!businessId) return;
    (async () => {
    
      setBusinessCurrency(getBusinessCurrency(businessId));
    })();
  }, [userDetails, loadBusinessCurrency, getBusinessCurrency]);

  const handleEditSubmit = async (updatedExpense) => {
    try {
      await useExpenseStore.getState().updateExpense({
        ExpenseID: updatedExpense.ExpenseID.toString(),
        TeamContactID: updatedExpense.TeamContactID.toString(),
        CategoryID: updatedExpense.CategoryID.toString(),
        Amount: updatedExpense.Amount.toString(),
        Description: updatedExpense.Description || '',
        ReceiptURL: updatedExpense.ReceiptURL || editExpense.ReceiptURL,
        ExpenseDate: new Date(updatedExpense.ExpenseDate).toISOString(),
      });

      // Fetch team expenses
      const store = useExpenseStore.getState();
      setExpenses(store.getTeamExpenses(updatedExpense.TeamContactID));

      // Fetch business expenses as well
      const businessId = userDetails?.BusinessID;
      if (businessId) {
        await store.loadExpensesByBusiness(businessId, { force: true });
      }

      setEditExpense(null);
    } catch (error) {
      console.error('❌ Error updating expense:', error);
    }
  };

  // Fuse.js setup
  // const fuse = new Fuse(expenses, {
  //   keys: ['CategoryName', 'ExpenseID','Amount','Description'],
  //   threshold: 0.4,
  // });

  // const filteredExpenses = search.trim()
  //   ? fuse.search(search).map(result => result.item)
  //   : expenses;

  // const statusFilteredExpenses = filteredExpenses.filter(
  //   (exp) => exp.ApprovalStatus?.toUpperCase() === filterStatus
  // );

  const fuse = new Fuse(expenses, {
  keys: ['CategoryName', 'ExpenseID', 'Amount', 'Description'],
  threshold: 0.4,
});

const filteredExpenses =
  search.trim().length >= 3
    ? fuse.search(search).map(result => result.item)
    : expenses;

const statusFilteredExpenses = filteredExpenses.filter(
  (exp) => exp.ApprovalStatus?.toUpperCase() === filterStatus
);


  useEffect(() => {
    if (viewingReceiptURL && viewingReceiptURL.toLowerCase().endsWith('.pdf')) {
      window.open(viewingReceiptURL, '_blank');
      setViewingReceiptURL(null);
    }
  }, [viewingReceiptURL]);

  // ✅ Show Loading spinner if still loading
//   if (loading) return <Loading />;
//   const getTruncatedFileName = (filename, maxLength = 30) => {
//   if (filename.length <= maxLength) return filename;
//   const start = filename.slice(0, 15);
//   const end = filename.slice(-10);
//   return `${start}...${end}`;
// };

const getStatusChipColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ width: '100%', boxSizing: 'border-box', bgcolor: 'white'}} >
                                                       {/* Filter Tabs */}
                             <Box
  sx={{
    position: 'sticky',
    top: { xs: 60, sm: 60 }, // Adjusted for taller mobile header
    zIndex: 1100,
    bgcolor: 'white',
    width: '100%',
    borderBottom: '1px solid #e0e0e0',
    boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
    marginTop: '0',
  }}
>
     <Box sx={{ mt: 2, mb: 2, ml: 2,mr: 2 }}>
        <TextField
          label="Search by Category or ID"
          variant="outlined"
          size="small"
          fullWidth
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </Box>
  <Tabs
    value={filterStatus}
    onChange={(e, newValue) => setFilterStatus(newValue)}
    variant="scrollable"
    scrollButtons={false}
    className="custom-tabs"
  >
    <StyledTab label="Pending" value="PENDING" />
    <StyledTab label="Approved" value="APPROVED" />
    <StyledTab label="Rejected" value="REJECTED" />
  </Tabs>
</Box>

      {/* Search Field */}
   

      {/* Expense List */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '16px',
        mt: 2
      }}>
        {statusFilteredExpenses.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No expenses to show.
          </Typography>
        ) : (
          statusFilteredExpenses.map((expense) => {
            const {
              ExpenseID,
              ExpenseDate,
              Amount,
              CategoryName,
              ApprovalStatus,
              Description,
              ReceiptURL,
              ApprovalComment,
            } = expense;

            return (
              <Card key={ExpenseID}  sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <CardContent className="expense-card-content">
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                        <Typography  sx={{ fontSize: 20}} >
    <strong>{CategoryName}</strong>
  </Typography>
                      <Typography color="text.secondary">ID: {ExpenseID}</Typography>
                    </Box>
                    <Chip label={ApprovalStatus?.toUpperCase()} color={getStatusChipColor(ApprovalStatus)} size="small" />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {new Date(ExpenseDate).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="h6" sx={{ mb: 1 }}>
                   <strong>{businessCurrency}{Amount}</strong> 
                  </Typography>

                  {Description && (
                    <Typography variant="body2">
                      <strong>Description:</strong> {Description}
                    </Typography>
                  )}

                  {['APPROVED', 'REJECTED'].includes(ApprovalStatus?.toUpperCase()) && ApprovalComment && (
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                      <strong>Reason:</strong> {ApprovalComment}
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{ p: 2, mt: 'auto', borderTop: '1px solid #e0e0e0', justifyContent: 'space-between' }}>
                  <Box>
                    {ReceiptURL && ReceiptURL !== 'null' && (
                      <Button
                        size="small"
                        className=''
                        onClick={() => {
                          
                            setViewingReceiptURL(ReceiptURL);
                          
                        }}
                          sx={{
    textTransform: 'none', // Prevents automatic capitalization
    fontSize: '0.875rem'   // Optional: Increase or change font size here
  }}
                      >
                        View Receipt
                      </Button>
                    )}
                  </Box>
                  <Box>
                    {ApprovalStatus?.toUpperCase() === 'PENDING' ? (
                      <>
                        <Button size="small" onClick={() => handleEdit(expense)}
                            sx={{
    textTransform: 'none', // Prevents automatic capitalization
    fontSize: '0.875rem'   // Optional: Increase or change font size here
  }}>
                          Edit
                        </Button>
                     <Button size="small" color="error" onClick={() => deleteExpenseLocal(ExpenseID)}
                            sx={{
    textTransform: 'none', // Prevents automatic capitalization
    fontSize: '0.875rem'   // Optional: Increase or change font size here
  }}>
                          Delete
                        </Button>
                      </>
                    ) : null}

                    {ApprovalStatus?.toUpperCase() === 'REJECTED' && (
                      <Button size="small" variant="contained" onClick={() => handleEdit(expense)}
                        sx={{
    textTransform: 'none', // Prevents automatic capitalization
    fontSize: '0.875rem'   // Optional: Increase or change font size here
  }}>
                        Resubmit
                      </Button>
                    )}
                  </Box>
                </CardActions>
              </Card>
            );
          })
        )}
      </Box>

      {/* Edit Expense Dialog */}
 <Dialog open={!!editExpense} onClose={() => setEditExpense(null)} fullWidth maxWidth="sm">
        <Box sx={{ p: 2 }}>
          {editExpense && (
            <ExpenseForm
              expense={editExpense}
              onSubmit={handleEditSubmit}
              onCancel={() => setEditExpense(null)}
            />
          )}
        </Box>
      </Dialog>

      {/* Receipt Image Viewer Dialog */}
      <Dialog
        open={!!viewingReceiptURL && !viewingReceiptURL.toLowerCase().endsWith('.pdf')}
        onClose={() => setViewingReceiptURL(null)}
        fullScreen
        sx={{ backgroundColor: 'rgba(0, 0, 0, 0.9)', zIndex: 1500 }}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
        }}
      >
        <IconButton
          onClick={() => setViewingReceiptURL(null)}
          sx={{
            
            position: 'absolute',
            top: 16,
            right: 10,
            width: 40, // or any equal width & height
            height: 40,
            borderRadius: '50%',
          
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.5)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
          }}
        >
          ✕
        </IconButton>
        <img
          src={viewingReceiptURL}
          alt="Receipt"
          style={{
            maxWidth: '90%',
            maxHeight: '90%',
            borderRadius: '8px',
            objectFit: 'contain',
            boxShadow: '0 4px 20px rgba(255,255,255,0.2)',
          }}
        />
      </Dialog>
    </Box>
  );
}
